import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IGetPermits, IPermitReq } from '../../models/permission.model';
import { RootState } from '../../store/rootReducer';
import _ from 'lodash';
import Loader from '../../components/Loader';
import SimpleHeader from '../../components/layout/SimpleHeader';
import Footer from '../../components/layout/Footer';
import Button from '../../components/Button';
import Radio from '../../components/Radio';
import styled from 'styled-components';
import useAxios from '../../hooks/useAxios';
import { IGetMenus } from '../../models/menus.model';
import { getMenus, getSubmenus } from '../../api/menus.api';
import { getPermits, createPermits } from '../../api/permission.api';
import ErrorResponse from '../../components/ErrorResponse';
import useNotification from '../../hooks/useNotification';
import useAuth from '../../hooks/useAuth';
import Split from 'react-split';

const Block = styled.div`
  .table-wrapper {
    margin-top: 25px;
  }
`;

const SystemAuth: FC = () => {
  const { writeAuthYn } = useAuth();
  const { t, i18n } = useTranslation();
  const { groups } = useSelector((state: RootState) => state.group);
  const { data: res, isLoading, error, execute } = useAxios<IGetMenus>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetMenus>();
  const {
    data: res3,
    isLoading: isLoading3,
    error: error3,
    execute: execute3,
  } = useAxios<IGetPermits>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios();
  const { infoNotification, successNotification } = useNotification();

  const [chosenGroup, setChosenGroup] = useState<number>(0);
  const [chosenMenu, setChosenMenu] = useState<number>(0);
  const [permitData, setPermitData] = useState<any>([]);

  useEffect(() => {
    execute(getMenus());
  }, []);

  useEffect(() => {
    if (res2 && !isLoading2 && res3 && !isLoading3) {
      const _menus = _.cloneDeep(res2.data);
      _menus.forEach((menu) => {
        const permit = res3.data?.find(
          (permit) => permit.menuCode === menu.menuCode,
        );
        if (typeof permit === 'undefined') {
          menu.writeAuthYn = 'X';
        } else {
          menu.writeAuthYn = permit.writeAuthYn;
        }
      });
      setPermitData(_menus);
    }
  }, [res2, isLoading2, res3, isLoading3]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      successNotification(t('system.auth.message4'));
    }
  }, [submitRes, submitIsLoading]);

  const onChooseGroup = (groupNo: number) => {
    setChosenGroup(groupNo);
    if (!chosenMenu) return;
    execute2(getSubmenus(chosenMenu));
    execute3(getPermits(groupNo, chosenMenu));
  };

  const onChooseMenu = (menuCode: number) => {
    setChosenMenu(menuCode);
    if (!chosenGroup) return;
    execute2(getSubmenus(menuCode));
    execute3(getPermits(chosenGroup, menuCode));
  };

  const onChangePermit = (index: number, value: string) => {
    if (!permitData) return;
    const _permit = [...permitData];
    _permit[index].writeAuthYn = value;
    setPermitData(_permit);
  };

  const onSubmit = async () => {
    if (permitData.length === 0) {
      return infoNotification(t('system.auth.message1'));
    }

    const req: IPermitReq[] = [];
    for (let i = 0; i < permitData.length; i++) {
      if (permitData[i].writeAuthYn === 'X') continue;
      req.push({
        menuCode: permitData[i].menuCode,
        writeAuthYn: permitData[i].writeAuthYn,
      });
    }

    submitExecute(createPermits(chosenGroup, chosenMenu, req));
  };

  return (
    <>
    <SimpleHeader
      iconFile="icon-system-big.svg"
      parentMenu={t('common.menu.system')}
      currentMenu={t('system.auth.title')}
      onlyTitle={true}
    />
    <Block className="content">
      {isLoading || isLoading2 || isLoading3 || submitIsLoading ? (
        <Loader />
      ) : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />
      <ErrorResponse error={submitError} />
      
      <Split
        className="splite-divide"
        sizes={[25, 25, 50]}
        minSize={100}
        expandToMin={false}
        gutterSize={10}
        gutterAlign="center"
        snapOffset={30}
        dragInterval={1}
        direction="horizontal"
        cursor="col-resize"
      >
        {/* first side */}
        <div className="col-frame">
          <div className="comm-bx__between">
            <div className="comm-sch_result">
            {t('system.auth.column4')}: <b>{groups?.length}</b>{t('common.text.unit')}
            </div>                  
          </div>
          <div className='comm-table__area'>
            <table>
              <thead>
                <tr>
                  <th>{t('system.group.column2')}</th>
                  <th>{t('system.group.column3')}</th>
                </tr>
              </thead>
              <tbody>
                {groups?.map((group) => (
                  <tr
                    key={group.groupNo}
                    className={
                      chosenGroup === group.groupNo ? 'chosen' : 'pointer'
                    }
                    onClick={() => onChooseGroup(group.groupNo)}
                  >
                    <td>{group.korName}</td>
                    <td>{group.engName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* second side */}
        <div className="col-frame">
          <div className="comm-bx__between">
            <h3 className="sub-title">{t('system.auth.text1')}</h3>                  
          </div>
          <div className='comm-table__area'>
            <table>
              <thead>
                <tr>
                  <th>{t('system.auth.column1')}</th>
                </tr>
              </thead>
              <tbody>
                {res?.data?.map((menu) => (
                  <tr
                    key={menu.menuCode}
                    className={
                      chosenMenu === menu.menuCode ? ' chosen' : 'pointer'
                    }
                    onClick={() => onChooseMenu(menu.menuCode)}
                  >
                    <td>{i18n.language === 'ko' ? menu.korName : menu.engName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* third side */}
        <div className="col-frame"> 
          <div className='comm-bx__between'>
            <h3 className="sub-title">{t('system.auth.text2')}</h3>     
          </div>
          <div className="comm-table__area">
            <table>
              <thead>
                <tr>
                  <th>{t('system.auth.column2')}</th>
                  <th>{t('system.auth.column3')}</th>
                </tr>
              </thead>
              <tbody>
                {permitData?.length ? (
                  permitData.map((permit: any, index: any) => (
                    <tr key={index}>
                      <td>{i18n.language === 'ko' ? permit.korName : permit.engName}</td>
                      <td>
                        <Radio
                          checked={permit.writeAuthYn === 'X'}
                          onChange={() => onChangePermit(index, 'X')}
                          text={t('system.auth.option1')}
                        />
                        <Radio
                          className="ml-4"
                          checked={permit.writeAuthYn === 'N'}
                          onChange={() => onChangePermit(index, 'N')}
                          text={t('system.auth.option2')}
                        />
                        <Radio
                          className="ml-4"
                          checked={permit.writeAuthYn === 'Y'}
                          onChange={() => onChangePermit(index, 'Y')}
                          text={t('system.auth.option3')}
                        />
                      </td>
                    </tr>
                  ))
                ) : chosenGroup && chosenMenu ? (
                  <tr>
                    <td colSpan={2}>{t('system.auth.message2')}</td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={2}>{t('system.auth.message3')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>   
          <div className="comm-bx__space">
            <div className="comm-btn__area  right">      
              <Button
                className="btn-basic pink"
                onClick={onSubmit}
                text={t('common.button.save')}
                writeAuthYn={writeAuthYn}
              />
            </div>
          </div>
        </div>
      </Split>
      <Footer />
    </Block>
    </>
  );
};

export default SystemAuth;
